import { KickAllIcon } from '@/components/KickIcons';
import { fxRunnerStateAtom, txConfigStateAtom } from '@/hooks/status';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import { useAtomValue } from 'jotai';
import { MegaphoneIcon, PowerIcon, PowerOffIcon, RotateCcwIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useOpenConfirmDialog, useOpenPromptDialog } from '@/hooks/dialogs';
import { ApiTimeout, useBackendApi } from '@/hooks/fetch';
import { useCloseAllSheets } from '@/hooks/sheets';
import { useAdminPerms } from '@/hooks/auth';
import { TxConfigState } from '@shared/enums';


const controlButtonsVariants = cva(
    `h-9 rounded-lg border border-fc-stroke bg-white/[0.04] text-fc-txt2
    flex flex-grow items-center justify-center flex-shrink-0
    transition-[background-color,border-color,color] duration-150
    focus:outline-none focus-visible:shadow-fc-focus disabled:opacity-35 disabled:pointer-events-none`,
    {
        variants: {
            type: {
                default: "hover:border-fc-accent/45 hover:bg-fc-accent/[0.16] hover:text-fc-accent-bright",
                destructive: "hover:border-fc-red/40 hover:bg-fc-red/[0.14] hover:text-fc-red-soft",
                warning: "hover:border-fc-yellow/40 hover:bg-fc-yellow/[0.12] hover:text-fc-yellow",
                success: "border-fc-green/35 bg-fc-green/[0.1] text-fc-green hover:bg-fc-green/[0.2]",
                info: "hover:border-fc-accent/45 hover:bg-fc-accent/[0.16] hover:text-fc-accent-bright",
            },
        },
        defaultVariants: {
            type: "default",
        },
    }
);

const noPermission = (subject: string) => (
    <p className="text-center text-fc-red-soft">
        Dir fehlt die Berechtigung, <br />
        {subject}.
    </p>
);

export default function ServerControls() {
    const txConfigState = useAtomValue(txConfigStateAtom);
    const fxRunnerState = useAtomValue(fxRunnerStateAtom);
    const openConfirmDialog = useOpenConfirmDialog();
    const openPromptDialog = useOpenPromptDialog();
    const closeAllSheets = useCloseAllSheets();
    const { hasPerm } = useAdminPerms();
    const fxsControlApi = useBackendApi({
        method: 'POST',
        path: '/fxserver/controls'
    });
    const fxsCommandsApi = useBackendApi({
        method: 'POST',
        path: '/fxserver/commands'
    });

    const handleServerControl = (action: 'start' | 'stop' | 'restart') => {
        const messageMap = {
            start: 'Server wird gestartet',
            stop: 'Server wird gestoppt',
            restart: 'Server wird neu gestartet',
        }
        const confirmMap = {
            start: 'starten',
            stop: 'stoppen',
            restart: 'neu starten',
        }
        const toastLoadingMessage = `${messageMap[action]} …`;
        const callApi = () => {
            closeAllSheets();
            fxsControlApi({
                data: { action },
                toastLoadingMessage,
                timeout: ApiTimeout.LONG,
            });
        }
        if (action === 'start') {
            callApi();
        } else {
            openConfirmDialog({
                title: messageMap[action],
                message: `Möchtest du den Server wirklich ${confirmMap[action]}?`,
                onConfirm: callApi,
            });
        }
    }
    const handleStartStop = () => {
        handleServerControl(fxRunnerState.isIdle ? 'start' : 'stop');
    }
    const handleRestart = () => {
        if (!fxRunnerState.isChildAlive) return;
        handleServerControl('restart');
    }

    const handleAnnounce = () => {
        if (!fxRunnerState.isChildAlive) return;
        openPromptDialog({
            title: 'Ankündigung senden',
            message: 'Die Nachricht wird an alle Spieler gesendet.',
            placeholder: 'Ankündigungstext',
            submitLabel: 'Senden',
            required: true,
            onSubmit: (input) => {
                closeAllSheets();
                fxsCommandsApi({
                    data: { action: 'admin_broadcast', parameter: input },
                    toastLoadingMessage: 'Ankündigung wird gesendet …',
                });
            }
        });
    }

    const handleKickAll = () => {
        if (!fxRunnerState.isChildAlive) return;
        openPromptDialog({
            title: 'Alle Spieler kicken',
            message: 'Grund eingeben oder leer lassen (Enter).',
            placeholder: 'Kick-Grund',
            submitLabel: 'Kicken',
            onSubmit: (input) => {
                closeAllSheets();
                fxsCommandsApi({
                    data: { action: 'kick_all', parameter: input },
                    toastLoadingMessage: 'Spieler werden gekickt …',
                });
            }
        });
    }

    const hasControlPerms = hasPerm('control.server');
    const hasAnnouncementPerm = hasPerm('announcement');

    if (txConfigState !== TxConfigState.Ready) {
        return (
            <div className='w-full text-center text-[11.5px] text-fc-txt3'>
                Server nicht konfiguriert.
            </div>
        )
    }
    return (
        <div className="flex flex-row justify-between gap-2">
            <Tooltip>
                <TooltipTrigger asChild>
                    {fxRunnerState.isIdle ? (
                        <button
                            onClick={handleStartStop}
                            className={cn(controlButtonsVariants({ type: 'success' }), 'shadow-[0_0_16px_rgb(158_255_219/0.25)]')}
                            disabled={!hasControlPerms}
                        >
                            <PowerIcon className='h-[18px]' />
                        </button>
                    ) : (
                        <button
                            onClick={handleStartStop}
                            className={controlButtonsVariants({ type: 'destructive' })}
                            disabled={!hasControlPerms}
                        >
                            <PowerOffIcon className='h-[18px]' />
                        </button>
                    )}
                </TooltipTrigger>
                <TooltipContent>
                    {hasControlPerms ? (
                        <p>{fxRunnerState.isIdle ? 'Server starten' : 'Server stoppen'}</p>
                    ) : noPermission('den Server zu steuern')}
                </TooltipContent>
            </Tooltip>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={handleRestart}
                        className={cn(controlButtonsVariants({ type: 'warning' }))}
                        disabled={!hasControlPerms || !fxRunnerState.isChildAlive}
                    >
                        <RotateCcwIcon className='h-[18px]' />
                    </button>
                </TooltipTrigger>
                <TooltipContent>
                    {hasControlPerms ? <p>Server neu starten</p> : noPermission('den Server zu steuern')}
                </TooltipContent>
            </Tooltip>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={handleKickAll}
                        className={controlButtonsVariants()}
                        disabled={!hasControlPerms || !fxRunnerState.isChildAlive}
                    >
                        <KickAllIcon style={{ height: '1.15rem', width: '1.4rem', fill: 'currentcolor' }} />
                    </button>
                </TooltipTrigger>
                <TooltipContent>
                    {hasControlPerms ? <p>Alle Spieler kicken</p> : noPermission('den Server zu steuern')}
                </TooltipContent>
            </Tooltip>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        onClick={handleAnnounce}
                        className={controlButtonsVariants()}
                        disabled={!hasAnnouncementPerm || !fxRunnerState.isChildAlive}
                    >
                        <MegaphoneIcon className='h-[18px]' />
                    </button>
                </TooltipTrigger>
                <TooltipContent>
                    {hasAnnouncementPerm ? <p>Ankündigung senden</p> : noPermission('Ankündigungen zu senden')}
                </TooltipContent>
            </Tooltip>
        </div>
    );
}
